import { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Group, Modal, ScrollArea, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

/** Mirrors backend `clean_dev_eui`: strip, uppercase, drop whitespace, ':' and '-'. */
export function cleanDevEui(raw: string): string {
  return raw.replace(/[\s:-]/g, "").toUpperCase();
}

export function isDevEui(raw: string): boolean {
  return /^[0-9A-F]{16}$/.test(cleanDevEui(raw));
}

/** Unique cleaned DevEUIs among the not-found values (no substring extraction). */
export function antennaCandidates(notFound: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const q of notFound) {
    if (!isDevEui(q)) continue;
    const c = cleanDevEui(q);
    if (seen.has(c)) continue;
    seen.add(c);
    out.push(c);
  }
  return out;
}

interface Props {
  opened: boolean;
  candidates: string[];
  loading?: boolean;
  onConfirm: (selected: string[]) => void;
  onCancel: () => void;
}

export function AntennaConfirmModal({ opened, candidates, loading, onConfirm, onCancel }: Props) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<string[]>(candidates);

  useEffect(() => {
    if (opened) setSelected(candidates);
  }, [opened, candidates]);

  function toggle(c: string, on: boolean) {
    setSelected((prev) => (on ? [...prev, c] : prev.filter((x) => x !== c)));
  }

  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={t("antennaConfirm.title")}
      closeOnClickOutside={false}
      size="md"
    >
      <Stack>
        <Alert color="yellow" variant="light">
          <Text size="sm">{t("antennaConfirm.text")}</Text>
        </Alert>
        <ScrollArea.Autosize mah={320}>
          <Stack gap={6}>
            {candidates.map((c) => (
              <Checkbox
                key={c}
                label={<Text ff="monospace" size="sm">{c}</Text>}
                checked={selected.includes(c)}
                onChange={(e) => toggle(c, e.currentTarget.checked)}
              />
            ))}
          </Stack>
        </ScrollArea.Autosize>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel} disabled={loading}>
            {t("antennaConfirm.cancel")}
          </Button>
          <Button
            onClick={() => onConfirm(candidates.filter((c) => selected.includes(c)))}
            disabled={selected.length === 0}
            loading={loading}
          >
            {t("antennaConfirm.create", { count: selected.length })}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Text for invalid / already-existing DevEUIs from a create-antennas response, or null. */
export function antennaSkippedMessage(
  t: (k: string, o?: Record<string, unknown>) => string,
  r: { invalid: string[]; exists: string[] },
): string | null {
  const parts: string[] = [];
  if (r.invalid.length > 0) parts.push(`${t("antennaConfirm.invalid")}: ${r.invalid.join(", ")}`);
  if (r.exists.length > 0) parts.push(`${t("antennaConfirm.exists")}: ${r.exists.join(", ")}`);
  return parts.length > 0 ? t("antennaConfirm.skipped", { list: parts.join("; ") }) : null;
}
